import * as z from 'zod';
import { Button, Field, Input, Stack, TagsInput, Text } from '@chakra-ui/react';
import { Client } from '@opanuj/shared/types';
import { toaster } from '../../../components/ui/toaster';
import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { withMask } from 'use-mask-input';
import { zodResolver } from '@hookform/resolvers/zod';

export type AccountFormProps = {
	client: Client;
	onUpdate: (data: Partial<Client>) => Promise<void>;
};

type AccountFormValues = {
	phone: string;
	notifications: {
		emails: string[];
	};
	billing: {
		name: string;
		address: string;
		postalCode: string;
		city: string;
		nip: string;
	};
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeNotificationEmails = (emails: string[], primaryEmail: string): string[] => {
	const normalizedPrimary = primaryEmail.trim();
	const uniqueEmails = Array.from(
		new Set(
			emails
				.map((email) => email.trim())
				.filter(Boolean)
				.filter((email) => email.toLowerCase() !== normalizedPrimary.toLowerCase())
				.filter((email) => isValidEmail(email))
		)
	);

	return normalizedPrimary ? [normalizedPrimary, ...uniqueEmails] : uniqueEmails;
};

const schema = z.object({
	phone: z
		.string()
		.min(1, { message: 'Pole wymagane' })
		.regex(
			/^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/,
			'Podaj poprawny numer telefonu'
		),
	notifications: z.object({
		emails: z
			.array(z.string().email('Podaj poprawny email'))
			.min(1, { message: 'Pole wymagane' })
			.default([]),
	}),
	billing: z.object({
		name: z.string().min(1, { message: 'Pole wymagane' }),
		address: z.string().min(1, { message: 'Pole wymagane' }),
		postalCode: z
			.string()
			.min(1, { message: 'Pole wymagane' })
			.regex(/^\d{2}-\d{3}$/, 'Podaj poprawny kod pocztowy (w formacie 00-000)'),
		city: z.string().min(1, { message: 'Pole wymagane' }),
		nip: z.union([z.string().regex(/^\d{10}$/, 'Podaj poprawny NIP (10 cyfr)'), z.literal('')]).optional(),
	}),
});

const AccountForm = ({ client, onUpdate }: AccountFormProps) => {
	const formattedClient = useMemo<AccountFormValues>(() => ({
		phone: client.phone ?? '',
		notifications: {
			emails: normalizeNotificationEmails(
				Array.isArray(client.notifications?.emails) ? client.notifications.emails : [],
				client.email ?? ''
			),
		},
		billing: {
			name: client.billing?.name ?? client.name ?? '',
			address: client.billing?.address ?? '',
			postalCode: client.billing?.postalCode ?? '',
			city: client.billing?.city ?? '',
			nip: client.billing?.nip ?? '',
		},
	}), [client]);

	const { handleSubmit, register, formState, setValue, watch } = useForm<AccountFormValues>({
		resolver: zodResolver(schema),
		defaultValues: formattedClient,
		values: formattedClient,
		mode: 'onChange',
	});
	const { isSubmitting, isDirty } = formState;
	const phoneField = register('phone');
	const postalCodeField = register('billing.postalCode');
	const notificationEmails = watch('notifications.emails') ?? formattedClient.notifications.emails;
	const attachMask = (fieldRef: ReturnType<typeof register>, mask: string) => (element: HTMLInputElement | null) => {
		fieldRef.ref(element);
		if (element) withMask(mask)(element);
	};

	const handleNotificationEmailsChange = (nextValues: string[]) => {
		const primaryEmail = client.email?.trim() || formattedClient.notifications.emails[0] || '';
		const uniqueValues = Array.from(
			new Set(
				nextValues
					.map((value) => value.trim())
					.filter(Boolean)
					.filter((value) => value.toLowerCase() !== primaryEmail.toLowerCase())
					.filter((value) => isValidEmail(value))
			)
		);
		const nextEmails = primaryEmail ? [primaryEmail, ...uniqueValues] : uniqueValues;
		setValue('notifications.emails', nextEmails, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const handleSubmitAdapter = async (values: AccountFormValues) => {
		const notificationEmails = normalizeNotificationEmails(values.notifications.emails, client.email ?? '');
		const payload: Partial<Client> = {
			...client,
			name: client.name,
			email: client.email,
			phone: values.phone,
			notes: client.notes,
			notifications: {
				...client.notifications,
				emails: notificationEmails,
			},
			billing: {
				...client.billing,
				name: values.billing.name,
				address: values.billing.address,
				postalCode: values.billing.postalCode,
				city: values.billing.city,
				...(values.billing.nip ? { nip: values.billing.nip } : client.billing?.nip ? { nip: client.billing.nip } : {}),
			},
		};

		try {
			await onUpdate(payload);
			toaster.create({
				title: 'Zapisano dane konta',
				type: 'success',
				duration: 2500,
			});
		} catch (error) {
			console.error('Failed to update account data', error);
			toaster.create({
				title: 'Nie udało się zapisać danych',
				type: 'error',
				duration: 3000,
			});
			throw error;
		}
	};

	return (
		<Stack m={4} gap={0} asChild maxW="400px" mt={10} mx="auto" alignItems="stretch">
			<form onSubmit={handleSubmit(handleSubmitAdapter)}>
				<Field.Root>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Imię i nazwisko
					</Field.Label>
					<Text mb={3} fontSize='lg' fontWeight='thin' textAlign="left">{client.name || '-'}</Text>
				</Field.Root>
				<Field.Root>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Email
					</Field.Label>
					<Text mb={3} fontSize='lg' fontWeight='thin' textAlign="left">{client.email || '-'}</Text>
				</Field.Root>

				<Text color='orange.600' mt={6} mb={2} textTransform='uppercase' letterSpacing="wide" textAlign="left">Dane kontaktowe</Text>
				<Field.Root required>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Telefon <Field.RequiredIndicator />
					</Field.Label>
					<Input {...phoneField} ref={attachMask(phoneField, '999 999 999')} />
				</Field.Root>
				<Field.Root required mt={3}>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Adresy e-mail <Field.RequiredIndicator />
					</Field.Label>
					<TagsInput.Root
						value={notificationEmails}
						onValueChange={(details) => handleNotificationEmailsChange(details.value)}
						validate={(details) => {
							const nextValue = details.inputValue.trim();
							return !nextValue || isValidEmail(nextValue);
						}}
						required
					>
						<TagsInput.Control>
							<TagsInput.Context>
								{({ value }) =>
									value.map((tag, index) => (
										<TagsInput.Item key={`${tag}-${index}`} index={index} value={tag}>
											<TagsInput.ItemPreview>
												<TagsInput.ItemText>{tag}</TagsInput.ItemText>
												<TagsInput.ItemDeleteTrigger display={index === 0 ? 'none' : 'inline-block'} aria-label={index === 0 ? 'Główny adres email jest wymagany' : 'Usuń adres email'} />
											</TagsInput.ItemPreview>
										</TagsInput.Item>
									))
								}
							</TagsInput.Context>
							<TagsInput.Input />
						</TagsInput.Control>
						<TagsInput.HiddenInput />
					</TagsInput.Root>
				</Field.Root>

				<Text color='orange.600' mt={6} textTransform='uppercase' letterSpacing="wide" mb={2}>Dane do faktury</Text>
				<Field.Root required>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Imię i nazwisko / Nazwa firmy <Field.RequiredIndicator />
					</Field.Label>
					<Input {...register('billing.name')} />
				</Field.Root>
				<Field.Root required mt={3}>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Adres <Field.RequiredIndicator />
					</Field.Label>
					<Input {...register('billing.address')} />
				</Field.Root>
				<Field.Root required mt={3}>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Kod pocztowy <Field.RequiredIndicator />
					</Field.Label>
					<Input {...postalCodeField} ref={attachMask(postalCodeField, '99-999')} />
				</Field.Root>
				<Field.Root required mt={3}>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						Miasto <Field.RequiredIndicator />
					</Field.Label>
					<Input {...register('billing.city')} />
				</Field.Root>
				<Field.Root mt={3}>
					<Field.Label fontSize="xs" fontWeight="semibold" color="gray.700">
						NIP
					</Field.Label>
					<Input {...register('billing.nip')} />
				</Field.Root>

				<Button
					mt={10}
					colorPalette="orange"
					loading={isSubmitting}
					disabled={!isDirty}
					w='full'
					type="submit"
				>
					Zapisz
				</Button>
			</form>
		</Stack>
	);
};

export default AccountForm;
